import enUS from './en-US.json';
import ptBR from './pt-BR.json';
import { createContext, useContext, useState } from 'react';
import React from 'react';

export type Language = 'en-US' | 'pt-BR';

const translations = {
  'en-US': enUS,
  'pt-BR': ptBR,
};

const LANGUAGE_STORAGE_KEY = 'xl-master-frontend-language';

function detectBrowserLanguage(): Language {
  const browserLang = navigator.language || navigator.languages?.[0];

  if (browserLang?.startsWith('pt')) {
    return 'pt-BR';
  }

  return 'en-US';
}

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    const savedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY) as Language;
    if (savedLanguage && savedLanguage in translations) {
      return savedLanguage;
    }

    const detectedLanguage = detectBrowserLanguage();

    localStorage.setItem(LANGUAGE_STORAGE_KEY, detectedLanguage);

    return detectedLanguage;
  });

  const setLanguage = (newLanguage: Language) => {
    setLanguageState(newLanguage);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, newLanguage);
  };

  return React.createElement(
    LanguageContext.Provider,
    { value: { language, setLanguage } },
    children
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

function translate(key: string, language: Language): string {
  const keys = key.split('.');
  let value: any = translations[language];

  for (const k of keys) {
    if (value && typeof value === 'object') {
      value = value[k];
    } else {
      return key;
    }
  }

  return value || key;
}

export function useTranslation() {
  const { language } = useLanguage();

  const t = (key: string): string => {
    return translate(key, language);
  };

  return { t, language };
}
